import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';

import CommunicationWorkflow from './CommunicationWorkflow';
import SnippetPreview from './SnippetPreview';
import SnippetAttributes from './SnippetAttributes';
import TemplateSelector from './TemplateSelector';
import { entity as mmsSnippets } from '../../ducks/mmsSnippets';
import MMSWorkflowDuck from '../../ducks/MMSWorkflowDuck';
import { MMS_FROALA_OPTIONS } from './SnippetEditor';

const mmsWorkflowActions = new MMSWorkflowDuck().actions();

class MMSWorkflow extends React.Component {
  static propTypes = {
    contactIds: PropTypes.array.isRequired,
    customer: PropTypes.object,
    form: PropTypes.object.isRequired,
    items: PropTypes.array,
    meta: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    previewContent: PropTypes.object.isRequired,
    previewRecipientId: PropTypes.string,
    snippets: PropTypes.array.isRequired,

    getCustomer: PropTypes.func.isRequired,
    getSnippets: PropTypes.func.isRequired,
    getTokens: PropTypes.func.isRequired,
    sendMMS: PropTypes.func.isRequired,
    setPreviewContact: PropTypes.func.isRequired,
    setSnippet: PropTypes.func.isRequired,
    validateSnippet: PropTypes.func.isRequired
  };

  static defaultProps = {
    contactIds: [],
    customer: {},
    form: {},
    items: [],
    meta: {},
    page: 0,
    previewContent: {},
    previewRecipientId: '',
    snippets: [],

    getCustomer: () => false,
    getSnippets: () => false,
    getTokens: () => false,
    sendMMS: () => false,
    setPreviewContact: () => false,
    setSnippet: () => false,
    validateSnippet: () => false
  };

  state = {
    templateSelector: '',
    error: null
  };

  render() {
    const {
      contactIds,
      form,
      model,
      page
    } = this.props;

    return <CommunicationWorkflow
      actions={mmsWorkflowActions}
      baseModel="mmsWorkflow.model"
      components={this.components()}
      confirmation={
        <MMSConfirm
          className="communication-workflow-modal-email-confirm"
          formPrefix=".mms"
          recipients={model.mms.recipients}
        />
      }
      header="Send text messages"
      ids={contactIds}
      model={model}
      form={form}
      page={page}
      recipientFilter={this.filterRecipients}
      communicationType={'text message'}
    />
  }

  components() {
    const {
      customer,
      form,
      getSnippets,
      meta,
      model,
      previewRecipientId,
      previewContent,
      setPreviewContact,
      snippets
    } = this.props;

    return [
      <SnippetAttributes
        className="communication-workflow-modal-form"
        prefix=""
        formPrefix=".mms.snippet"
        model={model.mms.snippet}
        form={form.mms.snippet}
        froalaOptions={{
          ...MMS_FROALA_OPTIONS,
          scrollableContainer: '.communication-workflow-modal-form',
        }}
        fields={[
          <TemplateSelector
            getSnippets={getSnippets}
            items={snippets}
            onChange={this.handleSnippetChange}
          />
        ]}
        ready={(
          meta && meta[this.state.templateSelector]
            ? meta && meta[this.state.templateSelector].ready
            : true
        )}
        showFormatting={false}
        onSubmit={this.handleAttributesSubmit}
        communicationType={'text message'}
        combineFamilyContacts={model.contacts && model.contacts.combine_family_contacts}
      />,
      <SnippetPreviewWithWarning
        className='communication-workflow-modal-email-preview'
        customer={customer}
        snippet={model.mms.snippet}
        recipients={model.mms.recipients}
        error={this.state.error}
        formPrefix='.mms'
        onSubmit={this.handlePreviewSubmit}
        previewRecipientId={previewRecipientId}
        previewContent={previewContent}
        setPreviewRecipientId={setPreviewContact}
        preventSubmit={
          model.mms.recipients.length === 0 || !previewContent.content.body
            || previewContent.content.body.trim().length === 0
        }
      />
    ];
  }

  componentDidMount() {
    const {
      getCustomer,
      getSnippets,
      getTokens
    } = this.props;

    getCustomer();
    getSnippets();
    getTokens();
  }

  // explicit false, nil counts as valid opt-in
  filterRecipients = (recipients) => recipients.filter((recipient) => recipient.cell_phone && recipient.cell_phone_opt_in !== false);
  handleAttributesSubmit = (values) => this.props.validateSnippet(values);
  handlePreviewSubmit = () => this.props.sendMMS(this.props.model.mms.recipients, this.props.model.mms.snippet)
    .catch((response) => {
      this.setState((prevState) => ({ ...prevState, error: response.message }));
      return Promise.reject(response.message);
    });
  handleSnippetChange = (snippet) => {
    this.props.setSnippet(snippet, this.props.customer);
    this.setState({ templateSelector: snippet.id });
  };
}

const displayTime = (seconds) => moment().startOf('day').add({s: seconds}).format('h:mm A');

const SnippetPreviewWithWarning = ({ customer, error, ...props }) => (
  <div>
    <div className='alert alert-info'>
      <strong>Note:</strong>
      &nbsp;MMS messages are only sent to recipients between the hours of&nbsp;

      <strong>{displayTime(customer.mms_start)}</strong>
      &nbsp;and&nbsp;
      <strong>{displayTime(customer.mms_stop)}</strong>

      &nbsp;in the school&apos;s time zone ({customer.time_zone}). Any messages sent outside of
      these hours will be held for delivery until the blackout period has ended.
    </div>
    {error && <div className='alert alert-danger'>{error}</div>}
    <MMSSnippetPreview {...props} />
  </div>
);

const MMSSnippetPreview = ({...props}) => <SnippetPreview {...props} renderHtml={false} header={
  (previewContent) => <div>
    <strong>Recipient: </strong>
    <span>{previewContent.recipient.name}&nbsp;({previewContent.recipient.cell_phone})</span>
  </div>
}/>;

const MMSConfirm = ({recipients, className}) => <div className={classNames(className, 'communication-modal-confirm')}>
  Text message sent to {recipients.length} recipient{recipients.length === 1 ? '' : 's'}.
</div>;


const mapStateToProps = (state) => {
  return {
    customer: state.customer.object,
    snippets: mmsSnippets.all(state),
    meta: state.mmsSnippets._meta,
    ...state.mmsWorkflow
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getCustomer: () => dispatch(mmsWorkflowActions.getCustomer()),
    getSnippets: () => dispatch(mmsSnippets.actions.load()),
    getTokens: () => dispatch(mmsWorkflowActions.getTokens()),
    sendMMS: (recipients, snippet) => dispatch(mmsWorkflowActions.sendMMS(recipients, snippet)),
    setPreviewContact: (contact, snippet) => dispatch(mmsWorkflowActions.setPreviewRecipientId(contact, snippet)),
    setSnippet: (snippet, customer) => dispatch(mmsWorkflowActions.setSnippet(snippet, customer)),
    validateSnippet: (snippet) => dispatch(mmsWorkflowActions.validateSnippet(snippet))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MMSWorkflow);
